/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useLayoutEffect, useRef, useEffect } from 'react';
import * as S from './ChooseApps.styled';
import {
  ModalBase,
  ModalHeader,
  ChooseBase,
  ModalFooter,
  ErrorComponent,
  Buttons,
  ErrorForBtn,
} from 'components';
import { collectAppsByGroups, createAppsData } from 'utils';
import { ChooseAppsContent } from './Content';
import { useToggleState } from './useToggleState';
import PropTypes from 'prop-types';
import { useFacade } from './useFacade';

const ChooseApps = ({ title, close, isOpen, allApps, selectedApps }) => {
  const [appsGroups] = useState(() => {
    const appsObject = createAppsData(collectAppsByGroups(allApps));
    return Object.entries(appsObject).map(([groupName, groupData]) => ({
      groupName,
      ...groupData,
    }));
  });
  const {
    onSaveHandler,
    filteredFaundedApps,
    search,
    setSearch,
    foundedAppGroups,
    handleClearSearchValue,
    faundedAppsIsFatching,
    faundedAppsIsSuccess,
    appsSelectedData$,
  } = useFacade(allApps, appsGroups, close);

  const {
    state: selectedAppsIds,
    toggle: toggleSelectedApps,
    isActive: isAppSelected,
    toggleOffAll: deselectAll,
    toggleOn: toggleSelectedAppsOn,
  } = useToggleState(selectedApps);

  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    let timer;
    if (uploadSuccess) {
      timer = setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [uploadSuccess]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      setFileError(false);
      setUploadSuccess(false);
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'csv') {
      setFileError(true);
      return;
    }

    setFileError(false);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/);
      const parsedValues = new Set();
      lines.forEach((line) => {
        const cells = line.split(/[;,]/);
        cells.forEach((cell) => {
          const trimmed = cell.trim();
          if (trimmed) {
            parsedValues.add(trimmed.toLowerCase());
          }
        });
      });
      const matchedApps = allApps.filter((app) => {
        const pbaNumStr = app.pba_number?.toString().toLowerCase();
        return parsedValues.has(pbaNumStr);
      });

      if (matchedApps.length === 0) {
        return;
      }

      setUploadSuccess(true);
      deselectAll();

      // Deduplicate matched apps by numeric pba_number to avoid double-toggling
      const uniqueMatchedAppsMap = new Map();
      matchedApps.forEach((app) => {
        const key = Number(app.pba_number);
        if (!isNaN(key) && !uniqueMatchedAppsMap.has(key)) {
          uniqueMatchedAppsMap.set(key, app);
        }
      });
      const uniqueMatchedApps = [...uniqueMatchedAppsMap.values()];
      const uniqueMatchedIds = uniqueMatchedApps.map((app) => Number(app.pba_number));

      toggleSelectedAppsOn(...uniqueMatchedIds);
      appsSelectedData$.value = [...appsSelectedData$.value, ...uniqueMatchedApps];
    };

    reader.readAsText(file);
  };

  const { length: countOfSelectedApps } = selectedAppsIds;

  const {
    state: { length: countOfOpenedGroups },
    toggle: toggleOpenedGroup,
    isActive: isGroupOpened,
    toggleOff: toggleGroupsOff,
    toggleOn: toggleGroupsOn,
    toggleOffAll: collapseAllGroups,
  } = useToggleState();

  const isGroupChecked = useCallback(
    (apps) => apps?.length > 0 && apps.map(({ pba_number }) => pba_number).every(isAppSelected),
    [isAppSelected],
  );
  const isSomeInGroupChecked = useCallback(
    (apps) => apps.map(({ pba_number }) => pba_number).some(isAppSelected),
    [isAppSelected],
  );

  useLayoutEffect(() => {
    if (search) {
      return toggleGroupsOn(...foundedAppGroups.map(({ group_name }) => group_name));
    }
    return foundedAppGroups.forEach(({ group_name, apps }) => {
      const somethingInGroupSelected = isSomeInGroupChecked(apps);
      const action = somethingInGroupSelected ? toggleGroupsOn : toggleGroupsOff;
      action(group_name);
    });
  }, [search, foundedAppGroups]);

  const handleSearchOnChange = (e) => {
    setSearch(e.target.value || '');
  };

  const toggleAppsGroup = (appsInGroup) => {
    const appsInGroupIds = appsInGroup.map(({ pba_number }) => pba_number);
    if (appsInGroupIds.every(isAppSelected)) {
      return toggleSelectedApps(...appsInGroupIds);
    }
    toggleSelectedApps(...appsInGroupIds.filter((groupId) => !isAppSelected(groupId)));
  };

  return (
    <ModalBase isOpen={isOpen}>
      <S.Box>
        <ModalHeader title={title} close={close} />
        <S.Content>
          <ChooseBase.OneSection
            showBtnDeselectAll={countOfSelectedApps > 0}
            isGroupOpen={countOfOpenedGroups > 0}
            handleDeselectAll={deselectAll}
            handleCollapseAll={collapseAllGroups}
            searchValue={search}
            searchOnChange={handleSearchOnChange}
            handleClearSearchValue={handleClearSearchValue}
          >
            {allApps.length ? (
              <ChooseAppsContent
                appsSelectedData$={appsSelectedData$}
                filteredFaundedApps={filteredFaundedApps}
                appsGroups={foundedAppGroups}
                toggleOpenedGroup={toggleOpenedGroup}
                isGroupOpened={isGroupOpened}
                toggleAppsGroup={toggleAppsGroup}
                isGroupChecked={isGroupChecked}
                isAppSelected={isAppSelected}
                toggleSelectedApps={toggleSelectedApps}
                search={search?.trim() || ''}
                faundedAppsIsFatching={faundedAppsIsFatching}
                faundedAppsIsSuccess={faundedAppsIsSuccess}
              />
            ) : (
              <ErrorComponent />
            )}
          </ChooseBase.OneSection>
        </S.Content>
        <ModalFooter
          saveButtonText='Apply'
          saveButtonOnClick={() => onSaveHandler(selectedAppsIds)}
          close={close}
          saveButtonDisabled={countOfSelectedApps === 0}
        >
          {title === 'Direct Buy Apps' && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <input
                type='file'
                accept='.csv'
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Buttons.SecondaryNormal onClick={handleUploadClick}>
                  Upload List
                </Buttons.SecondaryNormal>
                {uploadSuccess && (
                  <span
                    style={{
                      marginLeft: '12px',
                      fontSize: '14px',
                      color: '#1ab16f',
                      fontWeight: 500,
                    }}
                  >
                    Upload complete
                  </span>
                )}
              </div>
              <ErrorForBtn errorMsg='Only CSV files are allowed.' isOpen={fileError} />
            </div>
          )}
        </ModalFooter>
      </S.Box>
    </ModalBase>
  );
};

ChooseApps.propTypes = {
  title: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  allApps: PropTypes.array.isRequired,
  selectedApps: PropTypes.array.isRequired,
};

export default ChooseApps;
